import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  BankDetailsStatus,
  LandlordListUIModel,
  LandlordRowUIModel
} from '../../../shared/ui-models/landlord.ui-model';
import { Pagination } from '../../../shared/components/core-ui/pagination/pagination.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../shared/components/core-ui/button/button.component';
import { getLandlords, LandlordListParams } from '../../data.service';
import ImportBankDetails from './components/import-bank-details/import-bank-details.component';

type SortField = 'name' | 'bankDetailsUpdatedAt' | 'bankDetailsStatus';

const SEARCH_DEBOUNCE_MS = 400;

const FOCUS_RING =
  'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

const COLUMNS: {
  key: string;
  label: string;
  sortField?: SortField;
  width: string;
  align?: 'right';
}[] = [
  { key: 'name', label: 'Name', sortField: 'name', width: 'w-[24%]' },
  { key: 'landlordId', label: 'Reference', width: 'w-[12%]' },
  { key: 'contact', label: 'Contact', width: 'w-[22%]' },
  { key: 'unitsCount', label: 'Units', width: 'w-[8%]', align: 'right' },
  {
    key: 'bankDetails',
    label: 'Bank details',
    sortField: 'bankDetailsStatus',
    width: 'w-[16%]'
  },
  {
    key: 'lastUpdatedAt',
    label: 'Last updated',
    sortField: 'bankDetailsUpdatedAt',
    width: 'w-[18%]'
  }
];

function formatDate(value: string): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export const LandlordsListContainer: React.FC = () => {
  const history = useHistory();

  const [list, setList] = useState<LandlordListUIModel | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [bankDetailsMissing, setBankDetailsMissing] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isImportOpen, setIsImportOpen] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const latestRequest = useRef<number>(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, bankDetailsMissing, pageSize]);

  const params: LandlordListParams = useMemo(
    () => ({
      page,
      pageSize,
      search: search || undefined,
      bankDetailsMissing: bankDetailsMissing || undefined,
      sortBy,
      sortDirection
    }),
    [page, pageSize, search, bankDetailsMissing, sortBy, sortDirection]
  );

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    setIsLoading(true);
    setHasError(false);

    getLandlords(params).then((response) => {
      if (latestRequest.current !== requestId) {
        return;
      }

      if (isErrorInfo(response)) {
        setHasError(true);
        setList(null);
      } else {
        setList(response);
      }
      setIsLoading(false);
    });
  }, [params, reloadToken]);

  const handleSort = useCallback(
    (field?: SortField) => {
      if (!field) {
        return;
      }
      if (field === sortBy) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(field);
        setSortDirection('asc');
      }
    },
    [sortBy, sortDirection]
  );

  const openLandlord = useCallback(
    (landlordId: string) => {
      history.push(`${APP_ROUTES.LANDLORD_MODULE.FULL_PATH}/${landlordId}`);
    },
    [history]
  );

  const rows: LandlordRowUIModel[] = list ? list.rows : [];
  const missingCount = list ? list.totalMissingBankDetails : null;
  const activeCount = list ? list.total : null;

  const resultSummary = isLoading
    ? 'Loading landlords…'
    : hasError || activeCount === null
    ? ''
    : `${activeCount} ${activeCount === 1 ? 'landlord' : 'landlords'}${
        search ? ` matching “${search}”` : ''
      }`;

  return (
    <div className="p-8 font-gilroy-regular">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="m-0 text-4xl font-recoleta font-semibold text-neutral-500">Landlords</h1>
        <Button
          type={ButtonType.primary}
          size={ButtonSize.medium}
          onClick={() => setIsImportOpen(true)}
        >
          Import bank details
        </Button>
      </div>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-5 border-b border-gray-200 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div
            className="flex flex-wrap items-stretch gap-1 rounded-xl bg-creams-100 p-1"
            role="group"
            aria-label="Filter landlords"
          >
            <button
              type="button"
              aria-pressed={!bankDetailsMissing}
              onClick={() => setBankDetailsMissing(false)}
              className={`rounded-lg px-5 py-2.5 text-lg transition-colors ${FOCUS_RING} ${
                !bankDetailsMissing
                  ? 'bg-white font-gilroy-bold text-neutral-500 shadow-sm'
                  : 'font-gilroy-medium text-gray-600 hover:text-neutral-500'
              }`}
            >
              All landlords
            </button>
            <button
              type="button"
              aria-pressed={bankDetailsMissing}
              onClick={() => setBankDetailsMissing(true)}
              className={`rounded-lg px-5 py-2.5 text-lg transition-colors ${FOCUS_RING} ${
                bankDetailsMissing
                  ? 'bg-white font-gilroy-bold text-neutral-500 shadow-sm'
                  : 'font-gilroy-medium text-gray-600 hover:text-neutral-500'
              }`}
            >
              Missing bank details
              {missingCount !== null ? (
                <span
                  className={`ml-2 rounded-full px-2.5 py-0.5 text-base tabular-nums ${
                    missingCount > 0
                      ? 'bg-secondary-50 font-gilroy-bold text-secondary-700'
                      : 'font-gilroy-medium text-gray-600'
                  }`}
                >
                  {missingCount}
                </span>
              ) : null}
            </button>
          </div>

          <p
            aria-live="polite"
            className="m-0 text-lg font-gilroy-medium text-gray-600 lg:flex-1 lg:px-6"
          >
            {resultSummary}
          </p>

          <div className="lg:w-96">
            <label htmlFor="landlord-search" className="sr-only">
              Search landlords
            </label>
            <input
              id="landlord-search"
              type="search"
              className={`w-full rounded-xl border border-gray-200 px-4 py-3 text-lg font-gilroy-medium text-neutral-500 placeholder:text-gray-600 focus:border-primary-500 ${FOCUS_RING}`}
              placeholder="Search by name, email, phone or reference"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto" aria-busy={isLoading}>
          <table
            className="w-full min-w-[1080px] table-fixed border-separate border-spacing-0 text-left"
            aria-label="Landlords"
          >
            <colgroup>
              {COLUMNS.map((column) => (
                <col key={column.key} className={column.width} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((column) => {
                  const isSorted = !!column.sortField && sortBy === column.sortField;
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={
                        !column.sortField
                          ? undefined
                          : isSorted
                          ? sortDirection === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      className={`border-b border-gray-200 bg-creams-100 px-6 py-5 text-lg font-gilroy-bold text-neutral-500 ${
                        column.align === 'right' ? 'text-right' : ''
                      }`}
                    >
                      {column.sortField ? (
                        <button
                          type="button"
                          onClick={() => handleSort(column.sortField)}
                          className={`inline-flex items-center gap-1.5 rounded font-gilroy-bold hover:text-primary-600 ${FOCUS_RING}`}
                        >
                          {column.label}
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 10 6"
                            className={`h-1.5 w-2.5 shrink-0 transition-transform ${
                              isSorted
                                ? sortDirection === 'asc'
                                  ? 'rotate-180 text-primary-600'
                                  : 'text-primary-600'
                                : 'text-gray-300'
                            }`}
                          >
                            <path d="M0 0h10L5 6z" fill="currentColor" />
                          </svg>
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                [0, 1, 2, 3, 4].map((skeletonRow) => (
                  <tr key={`skeleton-${skeletonRow}`}>
                    {COLUMNS.map((column) => (
                      <td key={column.key} className="border-b border-gray-200 px-6 py-8">
                        <div className="h-4 w-full animate-pulse rounded bg-gray-200 motion-reduce:animate-none" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && hasError && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-6 py-16 text-center">
                    <p className="m-0 text-xl font-gilroy-medium text-neutral-500">
                      Landlords could not be loaded.
                    </p>
                    <div className="mt-5">
                      <Button
                        type={ButtonType.default}
                        size={ButtonSize.small}
                        onClick={() => setReloadToken(reloadToken + 1)}
                      >
                        Try again
                      </Button>
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading && !hasError && rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-6 py-16 text-center">
                    {search ? (
                      <>
                        <p className="m-0 text-xl font-gilroy-medium text-neutral-500">
                          No landlords match “{search}”.
                        </p>
                        <div className="mt-5">
                          <Button
                            type={ButtonType.default}
                            size={ButtonSize.small}
                            onClick={() => setSearchInput('')}
                          >
                            Clear search
                          </Button>
                        </div>
                      </>
                    ) : bankDetailsMissing ? (
                      <>
                        <p className="m-0 text-xl font-gilroy-bold text-primary-500">
                          Every landlord has bank details on file.
                        </p>
                        <div className="mt-5">
                          <Button
                            type={ButtonType.default}
                            size={ButtonSize.small}
                            onClick={() => setBankDetailsMissing(false)}
                          >
                            View all landlords
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="m-0 text-xl font-gilroy-medium text-neutral-500">
                        No landlords yet. Import bank details to add them.
                      </p>
                    )}
                  </td>
                </tr>
              )}

              {!isLoading &&
                !hasError &&
                rows.map((row) => {
                  const isMissing = row.bankDetailsStatus !== BankDetailsStatus.SET;
                  return (
                    <tr
                      key={row.landlordId}
                      onClick={() => openLandlord(row.landlordId)}
                      className="cursor-pointer text-xl font-gilroy-medium text-neutral-500 hover:bg-creams-100 focus-within:bg-creams-100"
                    >
                      <th
                        scope="row"
                        className="border-b border-gray-200 px-6 py-8 text-left align-top font-gilroy-bold"
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openLandlord(row.landlordId);
                          }}
                          className={`rounded text-left font-gilroy-bold text-neutral-500 hover:text-primary-600 hover:underline ${FOCUS_RING}`}
                        >
                          {row.name || 'Unnamed landlord'}
                        </button>
                      </th>
                      <td className="border-b border-gray-200 px-6 py-8 align-top text-gray-600 tabular-nums">
                        {row.landlordId}
                      </td>
                      <td className="border-b border-gray-200 px-6 py-8 align-top">
                        <div className="truncate" title={row.email || undefined}>
                          {row.email || '—'}
                        </div>
                        <div className="mt-1 text-lg text-gray-600 tabular-nums">
                          {row.phone || '—'}
                        </div>
                      </td>
                      <td className="border-b border-gray-200 px-6 py-8 align-top text-right tabular-nums">
                        {row.unitsCount}
                      </td>
                      <td className="border-b border-gray-200 px-6 py-8 align-top">
                        {isMissing ? (
                          <span className="inline-block rounded-full bg-secondary-50 px-3 py-1 text-lg font-gilroy-bold text-secondary-700">
                            Missing
                          </span>
                        ) : (
                          <span className="text-lg text-gray-600">Set</span>
                        )}
                      </td>
                      <td className="border-b border-gray-200 px-6 py-8 align-top text-lg text-gray-600 tabular-nums">
                        {formatDate(row.lastUpdatedAt)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </section>

      {!hasError && list && list.total > 0 && (
        <Pagination
          itemsPerPage={pageSize}
          itemCounts={list.total}
          offset={page}
          handlePageChange={(pageNumber: number) => setPage(pageNumber)}
          handleItemsPerPage={(itemsNumber: number) => setPageSize(itemsNumber)}
        />
      )}

      {isImportOpen && (
        <ImportBankDetails
          config={{ showModal: isImportOpen }}
          eventHandlers={{
            closeHandler: () => setIsImportOpen(false),
            importCompletedHandler: () => setReloadToken(reloadToken + 1)
          }}
        />
      )}
    </div>
  );
};

export default LandlordsListContainer;
